import { DollarSign } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";

/**
 * What the organizer said about admission.
 *
 * Amounts are held as strings because that is what the inputs produce, and a
 * half-typed "1" should not become a price of $1 in the draft. `toPriceArgs`
 * does the conversion once, at submit.
 */
export interface PriceValue {
  kind: "free" | "exact" | "range";
  amount: string;
  min: string;
  max: string;
  /** What the price buys — "person", "couple", or anything typed in. */
  unit: string;
}

/** Assumed when nothing else is said, and left off the listing as the norm. */
export const DEFAULT_PRICE_UNIT = "person";

/** Free text, so cap it — this lands in a listing, not a description field. */
const MAX_UNIT_LENGTH = 24;

export function defaultPriceValue(): PriceValue {
  return {
    kind: "exact",
    amount: "",
    min: "",
    max: "",
    unit: DEFAULT_PRICE_UNIT,
  };
}

const KINDS: { key: PriceValue["kind"]; label: string }[] = [
  { key: "free", label: "Free" },
  { key: "exact", label: "Set price" },
  { key: "range", label: "Range" },
];

const UNIT_PRESETS = ["person", "couple", "group"];

function parseAmount(raw: string): number | undefined {
  const n = Number(raw);
  return raw.trim() !== "" && Number.isFinite(n) && n >= 0 ? n : undefined;
}

/**
 * The `priceMin` / `priceMax` pair to store. Free is a min of 0 with no max;
 * an unparseable or empty amount stores nothing at all, so a blank field
 * reads as "not stated" rather than as free.
 */
export function toPriceArgs(value: PriceValue | null): {
  priceMin: number | undefined;
  priceMax: number | undefined;
  priceUnit: string | undefined;
} {
  if (!value) {
    return { priceMin: undefined, priceMax: undefined, priceUnit: undefined };
  }

  // Free needs no unit — free for one is free for two.
  if (value.kind === "free") {
    return { priceMin: 0, priceMax: undefined, priceUnit: undefined };
  }

  const priceUnit = normalizeUnit(value.unit);

  if (value.kind === "exact") {
    return {
      priceMin: parseAmount(value.amount),
      priceMax: undefined,
      priceUnit,
    };
  }

  const min = parseAmount(value.min);
  const max = parseAmount(value.max);
  // A backwards range is the same range typed in the other order.
  if (min !== undefined && max !== undefined && max < min) {
    return { priceMin: max, priceMax: min, priceUnit };
  }
  return { priceMin: min, priceMax: max, priceUnit };
}

/** Blank falls back to the default; anything else is trimmed and capped. */
function normalizeUnit(raw: string): string {
  const trimmed = raw.trim().slice(0, MAX_UNIT_LENGTH);
  return trimmed === "" ? DEFAULT_PRICE_UNIT : trimmed;
}

/**
 * The stored pair read back into form state, for editing an existing event.
 * An event with no price returns null, which leaves the section switched off.
 */
export function fromPriceArgs(
  priceMin?: number,
  priceMax?: number,
  priceUnit?: string
): PriceValue | null {
  if (priceMin === undefined && priceMax === undefined) return null;

  const unit = priceUnit?.trim() || DEFAULT_PRICE_UNIT;

  if (priceMin === 0 && priceMax === undefined) {
    return { ...defaultPriceValue(), kind: "free", unit };
  }

  if (priceMin !== undefined && priceMax !== undefined && priceMax !== priceMin) {
    return {
      kind: "range",
      amount: "",
      min: String(priceMin),
      max: String(priceMax),
      unit,
    };
  }

  const amount = priceMin ?? priceMax!;
  return { kind: "exact", amount: String(amount), min: "", max: "", unit };
}

/** Validation message for the submit form, or null when the value is usable. */
export function priceError(value: PriceValue | null): string | null {
  if (!value || value.kind === "free") return null;

  if (value.kind === "exact") {
    return parseAmount(value.amount) === undefined
      ? "Enter a price, or turn off price info."
      : null;
  }

  if (parseAmount(value.min) === undefined || parseAmount(value.max) === undefined) {
    return "Enter both ends of the range.";
  }
  return null;
}

interface PriceSectionProps {
  /** null = no price info given */
  value: PriceValue | null;
  onChange: (value: PriceValue | null) => void;
  error?: string;
}

export function PriceSection({ value, onChange, error }: PriceSectionProps) {
  const included = value !== null;

  const update = (patch: Partial<PriceValue>) => {
    if (!value) return;
    onChange({ ...value, ...patch });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <button
        type="button"
        onClick={() => onChange(included ? null : defaultPriceValue())}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2.5">
          <DollarSign className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Price</span>
        </div>
        <div
          className={cn(
            "relative w-10 h-6 rounded-full transition-colors duration-200",
            included ? "bg-primary" : "bg-secondary border border-border"
          )}
        >
          <div
            className={cn(
              "absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200",
              included ? "translate-x-5" : "translate-x-1"
            )}
          />
        </div>
      </button>

      {included && value && (
        <div className="space-y-3 pt-1">
          <div className="flex flex-wrap gap-1.5">
            {KINDS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                aria-pressed={value.kind === key}
                onClick={() => update({ kind: key })}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors",
                  value.kind === key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:bg-secondary"
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {value.kind === "exact" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Admission</Label>
              <AmountInput
                value={value.amount}
                onChange={(amount) => update({ amount })}
                placeholder="15"
              />
            </div>
          )}

          {value.kind === "range" && (
            <div className="flex items-end gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">From</Label>
                <AmountInput
                  value={value.min}
                  onChange={(min) => update({ min })}
                  placeholder="10"
                />
              </div>
              <span className="text-sm text-muted-foreground pb-2.5">to</span>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">To</Label>
                <AmountInput
                  value={value.max}
                  onChange={(max) => update({ max })}
                  placeholder="20"
                />
              </div>
            </div>
          )}

          {/* What the price buys. Almost always per head, but a private
              lesson billed per couple costs the same for one dancer or two,
              so the listing has to be able to say which. */}
          {value.kind !== "free" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Per</Label>
              <div className="flex flex-wrap items-center gap-1.5">
                {UNIT_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    aria-pressed={value.unit.trim().toLowerCase() === preset}
                    onClick={() => update({ unit: preset })}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors capitalize",
                      value.unit.trim().toLowerCase() === preset
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground border-border hover:bg-secondary"
                    )}
                  >
                    {preset}
                  </button>
                ))}
                <Input
                  value={value.unit}
                  onChange={(e) => update({ unit: e.target.value })}
                  maxLength={MAX_UNIT_LENGTH}
                  placeholder="or type your own"
                  className="w-40"
                  aria-label="Price per"
                />
              </div>
            </div>
          )}

          {value.kind === "range" && (
            <p className="text-xs text-muted-foreground">
              Use a range when the door price varies — student and member rates,
              or lesson-plus-dance versus dance only.
            </p>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}

      {!included && (
        <p className="text-xs text-muted-foreground">
          Leave off if the price isn't set. Events show "Price not listed" rather
          than implying they're free.
        </p>
      )}
    </div>
  );
}

function AmountInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-28">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
        $
      </span>
      <Input
        type="number"
        min={0}
        step="0.01"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-7"
      />
    </div>
  );
}
